// File: src/components/admin/UserDetail/UserPlatformConnectionsTab.js
// Per-user platform connection status in admin detail view

import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography, Grid, Box, Chip, Card, CardContent,
    CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import {
    CheckCircle as ConnectedIcon,
    WarningAmber as WarningIcon,
    Cancel as DisconnectedIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminApi } from '../../../services/adminApi';

const STATUS_CONFIG = {
    connected: { label: 'Connected', color: 'success', icon: ConnectedIcon },
    auth_expired: { label: 'Connection Expired', color: 'warning', icon: WarningIcon },
    disconnected: { label: 'Disconnected', color: 'default', icon: DisconnectedIcon },
};

const PLATFORM_NAMES = {
    'google-ads': 'Google Ads',
    'microsoft-ads': 'Microsoft Ads',
    'meta-ads': 'Meta Ads',
};

const UserPlatformConnectionsTab = ({ uid }) => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadIntegrations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminApi.getUserIntegrations(uid);
            setIntegrations(data.integrations || []);
        } catch (err) {
            console.error('[UserPlatformConnectionsTab] Failed to load:', err);
            setError(err.message || 'Failed to load platform connections');
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => {
        loadIntegrations();
    }, [loadIntegrations]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }

    return (
        <Grid container spacing={2} data-testid="user-platform-connections-tab">
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" gutterBottom>
                        Platform Connections
                    </Typography>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={loadIntegrations}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Grid>

            {integrations.length === 0 ? (
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                        No platform connections found for this user.
                    </Typography>
                </Grid>
            ) : (
                integrations.map((integration) => {
                    const config = STATUS_CONFIG[integration.status] || STATUS_CONFIG.disconnected;
                    const StatusIcon = config.icon;
                    const platformName = PLATFORM_NAMES[integration.platformId] || integration.platformId;

                    return (
                        <Grid item xs={12} sm={6} key={integration.platformId}>
                            <Card variant="outlined" data-testid={`admin-platform-${integration.platformId}`}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {platformName}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            icon={<StatusIcon sx={{ fontSize: 16 }} />}
                                            label={config.label}
                                            color={config.color}
                                            data-testid={`admin-platform-status-${integration.platformId}`}
                                        />
                                    </Box>

                                    {integration.selectedAccount && (
                                        <Typography variant="body2" color="text.secondary">
                                            Account: {integration.accounts?.find(a => a.id === integration.selectedAccount)?.name || integration.selectedAccount}
                                        </Typography>
                                    )}

                                    {integration.accounts?.length > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            {integration.accounts.length} account{integration.accounts.length !== 1 ? 's' : ''} linked
                                        </Typography>
                                    )}

                                    {integration.healthError && (
                                        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                                            <Typography variant="caption">{integration.healthError}</Typography>
                                        </Alert>
                                    )}

                                    {integration.healthUpdatedAt && (
                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                            Health updated: {new Date(integration.healthUpdatedAt).toLocaleString()}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })
            )}
        </Grid>
    );
};

export default UserPlatformConnectionsTab;
